/* eslint-disable jsdoc/require-jsdoc */
import axios, { AxiosResponse } from 'axios';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { summaryAPIProps } from '../constants/telosair-interfaces';

const PUBLIC_TOKEN_DOMAIN_URL = `${DEV_NEW_DOMAIN_URL}/public-tokens`;
const PUBLIC_DOMAIN_URL = `${DEV_NEW_DOMAIN_URL}/public`;
/**
 * Creates axios instance with public token from sessionStorage.
 * @returns Axios instance with authorization header.
 */
const getPublicAxiosInstance = (): ReturnType<typeof axios.create> => {
  const token = sessionStorage.getItem('token');
  return axios.create({
    headers: {
      Authorization: token ? `Bearer ${token}` : ''
    }
  });
};

/**
 * Validates the public token.
 * @returns Validation response.
 */
export const validatePublicToken = (): Promise<AxiosResponse<any, any>> => {
  const axiosInstance = getPublicAxiosInstance();
  return axiosInstance.get(`${PUBLIC_TOKEN_DOMAIN_URL}/validate`);
};

/**
 * Fetches list of parameters for public view.
 * @returns - Parameters list.
 */
export const getParametersList = (): Promise<AxiosResponse<any>> => {
  const axiosInstance = getPublicAxiosInstance();
  return axiosInstance.get(`${PUBLIC_DOMAIN_URL}/parameters`);
};

/**
 * Get summary of the device for public view.
 * @param payload - Payload of the API.
 * @returns Device summary data.
 */
export const getDeviceSummary = (payload: summaryAPIProps): Promise<AxiosResponse<any, any>> => {
  const axiosInstance = getPublicAxiosInstance();
  return axiosInstance.post(`${PUBLIC_DOMAIN_URL}/device/summary`, payload);
};

/**
 * Get summary of the group for public view.
 * @param payload - Payload of the API.
 * @returns Group summary data.
 */
export const getGroupSummary = (payload: summaryAPIProps): Promise<AxiosResponse<any, any>> => {
  const axiosInstance = getPublicAxiosInstance();
  return axiosInstance.post(`${PUBLIC_DOMAIN_URL}/group/summary`, payload);
};

/**
 * Get data summary for public view.
 * @param payload - Payload of the API.
 * @returns Data summary.
 */
export const getDataSummary = (payload: summaryAPIProps): Promise<AxiosResponse<any, any>> => {
  const axiosInstance = getPublicAxiosInstance();
  return axiosInstance.post(`${PUBLIC_DOMAIN_URL}/summary`, payload);
};

/**
 * Get device summary over time for public view.
 * @param payload - Payload of the API.
 * @param options - Optional config.
 * @param options.signal - AbortSignal to cancel the request.
 * @returns Summary over time data.
 */
export const getDeviceSummaryOverTime = (
  payload: summaryAPIProps & { devices?: number[] },
  options?: { signal?: AbortSignal }
): Promise<AxiosResponse<any, any>> => {
  const axiosInstance = getPublicAxiosInstance();
  return axiosInstance.post(
    `${PUBLIC_DOMAIN_URL}/summaryOverTime`,
    { ...payload, devices: payload.devices || [] },
    { signal: options?.signal }
  );
};

/**
 * Get all devices for public view.
 * @returns List of devices.
 */
export const getAllDevices = (): Promise<AxiosResponse<any, any>> => {
  const axiosInstance = getPublicAxiosInstance();
  return axiosInstance.get(`${PUBLIC_DOMAIN_URL}/device`);
};

/**
 * Get all groups for public view.
 * @returns List of groups.
 */
export const getAllGroups = (): Promise<AxiosResponse<any, any>> => {
  const axiosInstance = getPublicAxiosInstance();
  return axiosInstance.get(`${PUBLIC_DOMAIN_URL}/groups`);
};
